import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { SimulateInboundResult } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { useEmailMode } from "@/components/EmailModeBanner";

/**
 * Rehearsal controls, shown only on a deployment where nothing is delivered.
 *
 * <p>The sandbox provider emits no webhooks and receives no mail, so without
 * these a rehearsed conversation stops dead after the first message: no reply
 * ever arrives and no phase ever advances. This supplies the missing stimulus
 * as an explicit operator action. The injected reply travels the real inbound
 * pipeline — threading, prospect resolution, LLM analysis, transition
 * evaluation — so what it proves about the system is the same thing a real
 * reply would prove.
 */
export default function SandboxProspectTools({ prospectId }: { prospectId: string }) {
  const { data: mode } = useEmailMode();
  const queryClient = useQueryClient();
  const [bodyText, setBodyText] = useState("");
  const [subject, setSubject] = useState("");

  const simulate = useMutation({
    mutationFn: () =>
      api.post<SimulateInboundResult>(`/api/v1/sandbox/prospects/${prospectId}/simulate-inbound`, {
        subject: subject.trim() || null,
        bodyText,
      }),
    onSuccess: () => {
      setBodyText("");
      setSubject("");
      queryClient.invalidateQueries({ queryKey: ["prospect", prospectId] });
      queryClient.invalidateQueries({ queryKey: ["prospect-threads", prospectId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages"] });
      queryClient.invalidateQueries({ queryKey: ["emails"] });
    },
  });

  // Absent on any deployment that actually sends mail — there is nothing to
  // simulate there, and offering the control would invite fabricated history.
  if (!mode?.simulationAvailable) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/40">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-amber-700" aria-hidden />
        <CardTitle>Simulate a reply (sandbox only)</CardTitle>
      </div>
      <p className="mb-3 text-xs text-[var(--fg-muted)]">
        Injects a reply as though this prospect had sent it, threaded against the
        last message the agent sent. It runs the real inbound pipeline: the agent
        will analyse it and may advance the phase.
      </p>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!bodyText.trim()) return;
          simulate.mutate();
        }}
      >
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject (defaults to Re: the last message)"
          className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1.5 text-sm"
        />
        <textarea
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          rows={4}
          placeholder="What the prospect replies…"
          className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1.5 text-sm"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!bodyText.trim() || simulate.isPending}
            className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {simulate.isPending ? "Injecting…" : "Inject reply"}
          </button>
          {simulate.data ? (
            <span
              className={
                simulate.data.ingested
                  ? "text-sm text-[var(--success)]"
                  : "text-sm text-[var(--danger)]"
              }
            >
              {simulate.data.note}
            </span>
          ) : null}
          {simulate.error ? (
            <span className="text-sm text-[var(--danger)]">
              {simulate.error instanceof ApiError && simulate.error.status === 403
                ? "Requires an administrator account."
                : "Injection failed."}
            </span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
